import { FC } from "react";
import { Box, Radio, RadioGroup, Typography } from "@mui/material";

import CardChip from "../CardChip";
import { getManualPaymentFields } from "../record-payment/manualPayments";
import { RemitCandidate, summarizePayment } from "./joinPayments";

// The 835's identifiers, the ones the backend matches a manual payment on
const describeIdentifiers = (candidate: RemitCandidate): string => {
  const { checkOrEftNumber, payerClaimNumber } = getManualPaymentFields(
    candidate.remit,
  );
  return [
    checkOrEftNumber && `Check / EFT # ${checkOrEftNumber}`,
    payerClaimNumber && `Payer claim # ${payerClaimNumber}`,
  ]
    .filter(Boolean)
    .join(" · ");
};

const RemitOption: FC<{
  candidate: RemitCandidate;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}> = ({ candidate, selected, disabled, onSelect }) => {
  const { remit, matchReasons, isJoined } = candidate;
  const identifiers = describeIdentifiers(candidate);
  const isDisabled = disabled || isJoined;

  return (
    <Box
      component="label"
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        p: 1.25,
        borderRadius: "8px",
        border: "1px solid",
        borderColor: selected ? "primary.main" : "divider",
        cursor: isDisabled ? "default" : "pointer",
        opacity: isJoined ? 0.6 : 1,
      }}
    >
      <Radio
        size="small"
        checked={selected}
        disabled={isDisabled}
        onChange={onSelect}
        value={remit.attachmentId}
        sx={{ p: 0.5 }}
        inputProps={{ "aria-label": `835 Payment, ${summarizePayment(remit)}` }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
            835 Payment
          </Typography>
          {isJoined ? (
            <CardChip chip={{ label: "Already joined", kind: "default" }} />
          ) : matchReasons.length > 0 ? (
            <CardChip chip={{ label: "Likely match", kind: "success" }} />
          ) : null}
        </Box>
        <Typography sx={{ fontSize: "0.8125rem" }}>
          {summarizePayment(remit)}
        </Typography>
        {identifiers && (
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {identifiers}
          </Typography>
        )}
        {isJoined ? (
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            Another manual payment is joined to this 835.
          </Typography>
        ) : (
          matchReasons.length > 0 && (
            <Typography sx={{ fontSize: "0.75rem", color: "success.main" }}>
              {matchReasons.join(" · ")}
            </Typography>
          )
        )}
      </Box>
    </Box>
  );
};

/** The claim's electronic remits a manual payment can be joined to, likely matches first. */
const RemitPicker: FC<{
  candidates: RemitCandidate[];
  selectedId: string | null;
  onSelect: (remitAttachmentId: string) => void;
  disabled?: boolean;
}> = ({ candidates, selectedId, onSelect, disabled = false }) => (
  <RadioGroup
    aria-label="Electronic remits"
    value={selectedId ?? ""}
    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
  >
    {candidates.map((candidate) => {
      const id = candidate.remit.attachmentId as string;
      return (
        <RemitOption
          key={id}
          candidate={candidate}
          selected={selectedId === id}
          disabled={disabled}
          onSelect={() => onSelect(id)}
        />
      );
    })}
  </RadioGroup>
);

export default RemitPicker;
